import { useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { apiCall } from './api/client';
import LoginPage from './pages/LoginPage';
import WorklistPage from './pages/WorklistPage';
import ViewerPage from './pages/ViewerPage';
import EmptyViewerPage from './pages/EmptyViewerPage';
import ReportPage from './pages/ReportPage';
import TemplatePage from './pages/TemplatePage';
import HeaderDesignerPage from './pages/HeaderDesignerPage';
import MacrosPage from './pages/MacrosPage';
import { useAutoLoadPlugins } from './plugins/runtime/hooks';

const HEARTBEAT_INTERVAL = 60_000; // 60 seconds

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const acctoken = useAuthStore((s) => s.acctoken);
  if (!acctoken) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/** Sends a heartbeat every 60s to keep the session alive */
function SessionHeartbeat() {
  const acctoken = useAuthStore((s) => s.acctoken);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!acctoken) return;

    async function beat() {
      const res = await apiCall('session.heartbeat');
      if (res.s === 'error') {
        // Session expired on server — force logout
        logout();
        navigate('/login', { replace: true });
      }
    }

    // First heartbeat immediately to extend the token after login
    beat();

    intervalRef.current = setInterval(beat, HEARTBEAT_INTERVAL);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [acctoken]);

  return null;
}

export default function App() {
  // Boot the runtime plugin registry once per session. Pulls
  // userSettings.plugins and fetches /manifest from each enabled sidecar.
  // Failures land in each plugin's loadError so Settings can show them;
  // the rest of the app renders fine either way (plugins degrade to
  // nothing-mounted, not crashes).
  useAutoLoadPlugins();
  return (
    <>
      <SessionHeartbeat />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/worklist"
          element={
            <ProtectedRoute>
              <WorklistPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/viewer/:studyId"
          element={
            <ProtectedRoute>
              <ViewerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/viewer-empty"
          element={
            <ProtectedRoute>
              <EmptyViewerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/report/:studyId"
          element={
            <ProtectedRoute>
              <ReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/template/:templateId"
          element={
            <ProtectedRoute>
              <TemplatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/header"
          element={
            <ProtectedRoute>
              <HeaderDesignerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/macros"
          element={
            <ProtectedRoute>
              <MacrosPage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/worklist" replace />} />
      </Routes>
    </>
  );
}
